import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { updateOtherFinancialItem } from '@/modules/Report/components/OtherFinancials/services'
import {
    OtherFinancialItem,
    UpdateOtherFinancialItemRequest,
} from '@/modules/Report/components/OtherFinancials/types'
import { Dialog, Button, FormItem, Typogaphy } from 'vnetwork-ui'
import { ModalProps } from '@/types/shared'

export interface UpdateOtherFinancialModalProps extends ModalProps {
    financialItem: OtherFinancialItem
    refetch?: () => void
}

interface FormData {
    item_name: string
    amount: number
    currency: string
    notes?: string
    description?: string
}

const UpdateOtherFinancialModal: React.FC<UpdateOtherFinancialModalProps> = ({
    open,
    onClose,
    financialItem,
    refetch,
}) => {
    const { t } = useTranslation()

    // Form schema - only editable fields
    const schema = useMemo(
        () =>
            yup.object().shape({
                item_name: yup.string().required(t('required')),
                amount: yup
                    .number()
                    .min(0, t('must_be_zero_or_positive'))
                    .required(t('required')),
                currency: yup.string().required(t('required')),
                notes: yup.string(),
                description: yup.string(),
            }),
        [t]
    )

    const defaultValues: FormData = useMemo(
        () => ({
            item_name: financialItem.item_name,
            amount: financialItem.amount,
            currency: financialItem.currency,
            notes: financialItem.notes || '',
            description: financialItem.description || '',
        }),
        [financialItem]
    )

    const formControl = useForm<FormData>({
        defaultValues,
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when financialItem changes
    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [open, financialItem, reset, defaultValues])

    // Update mutation
    const { mutate: update, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                id,
                data,
            }: {
                id: number
                data: UpdateOtherFinancialItemRequest
            }) => updateOtherFinancialItem(id, data),
        },
        {
            reactQueryKeys: [QUERY_KEYS.REPORTS_BI_OTHER_FINANCIALS],
            onSuccess: () => {
                refetch?.()
                onClose()
            },
        }
    )

    const onSubmit = (values: FormData) => {
        update({
            id: financialItem.id,
            data: {
                item_name: values.item_name,
                amount: values.amount,
                currency: values.currency,
                notes: values.notes,
                description: values.description,
            },
        })
    }

    const isRevenue = financialItem.item_type === 'revenue'

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit_other_financial_item')}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
                {/* Display read-only info */}
                <div className="bg-gray-50 p-4 rounded-md space-y-2">
                    <div className="flex justify-between">
                        <Typogaphy className="text-gray-600">
                            {t('item_type')}:
                        </Typogaphy>
                        <Typogaphy
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                isRevenue
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                            }`}
                        >
                            {isRevenue ? t('revenue') : t('expense')}
                        </Typogaphy>
                    </div>
                    <div className="flex justify-between">
                        <Typogaphy className="text-gray-600">
                            {t('report_month')}:
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {financialItem.report_month}
                        </Typogaphy>
                    </div>
                </div>

                {/* Editable fields */}
                <FormItem
                    control={control}
                    name="item_name"
                    type="textfield"
                    label={t('item_name')}
                />

                <FormItem
                    control={control}
                    name="amount"
                    type="number"
                    label={t('amount')}
                />

                <FormItem
                    control={control}
                    name="currency"
                    type="textfield"
                    label={t('currency')}
                />

                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                />

                <FormItem
                    control={control}
                    name="description"
                    type="textarea"
                    label={t('description')}
                />

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isPending}
                        loading={isPending}
                    >
                        {t('update')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default UpdateOtherFinancialModal
